import * as React from "react";
import { useScreenState } from "../ScreenState";

const RewardsCenter = React.lazy(() => import("./RewardsCenter"));

type RewardsCenterContextType = {
  isOpen: boolean;
  openRewardCenter: () => void;
  closeRewardCenter: () => void;
};

const RewardsCenterContext = React.createContext<
  RewardsCenterContextType | undefined
>(undefined);

export const RewardsCenterProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [doForceDesktopScreenSize, resetScreenSize] = useScreenState();
  const [isOpen, setOpen] = React.useState(false);

  const closeRewardCenter = React.useCallback(() => {
    if (doForceDesktopScreenSize) {
      resetScreenSize();
    }
    setOpen(false);
  }, [doForceDesktopScreenSize, resetScreenSize]);

  const openRewardCenter = React.useCallback(() => setOpen(true), []);

  const contextValue = React.useMemo(
    () => ({ isOpen, openRewardCenter, closeRewardCenter }),
    [isOpen, openRewardCenter, closeRewardCenter]
  );

  return (
    <RewardsCenterContext.Provider value={contextValue}>
      {children}
      {isOpen && (
        <React.Suspense fallback={null}>
          <RewardsCenter />
        </React.Suspense>
      )}
    </RewardsCenterContext.Provider>
  );
};

export const useRewardsCenter = () => {
  const context = React.useContext(RewardsCenterContext);
  if (!context) {
    throw new Error(
      "useRewardsCenter must be used within RewardsCenterProvider"
    );
  }
  return context;
};
